import { markRaw } from "vue";
import ApexCharts from "apexcharts";
import VueApexCharts from "vue-apexcharts";
import { chartDomain, sampleChartSeries } from "../utils/chartSampling";
import { fullChartCSV } from "../utils/chartCsv";
import { downloadFile } from "../utils/files";

// Keep the public vue-apexcharts interface, but coalesce updates and keep the
// mutable renderer out of Vue's deep observation. Raw points remain available
// for zoom and CSV; only the rendered datetime series are reduced.
export default {
  ...VueApexCharts,
  name: "PerformanceApexChart",
  created() {
    this._hiddenSeries = new Set();
    this._extraOptions = {};
    this._range = null;
    this._queued = false;
    this._updating = false;
    this.$watch("series", () => { this._suppliedSeries = null; this.queueUpdate(); });
    this.$watch("options", this.queueUpdate);
    ["type", "width", "height"].forEach(key => this.$watch(key, this.queueUpdate));
  },
  mounted() {
    // The Apex toolbar creates a fresh exporter on each click. Capture CSV
    // clicks before its handler so exports use raw points instead of SVG data.
    this._csvClick = event => {
      if (!event.target.closest?.(".exportCSV")) return;
      const csv = this.buildCSV();
      if (csv === null) return;
      event.preventDefault(); event.stopImmediatePropagation();
      this.downloadCSV(csv);
    };
    this.$el.addEventListener("click", this._csvClick, true);
    this._visibilityHandler = () => { if (!document.hidden) this.queueUpdate(); };
    document.addEventListener("visibilitychange", this._visibilityHandler);
    if (typeof ResizeObserver !== "undefined") {
      this._resizeObserver = new ResizeObserver(() => {
        const width = Math.round(this.$el.clientWidth);
        if (width !== this._sampleWidth) { this._sampleWidth = width; this.queueUpdate(); }
      });
      this._resizeObserver.observe(this.$el);
    }
    this.init();
  },
  beforeDestroy() {
    this.$el.removeEventListener("click", this._csvClick, true);
    document.removeEventListener("visibilitychange", this._visibilityHandler);
    if (this._resizeObserver) this._resizeObserver.disconnect();
    this._disposed = true;
    if (this.chart && !this._updating) this.destroy();
  },
  methods: {
    ...VueApexCharts.methods,
    createChart(config) { return new ApexCharts(this.$el, config); },
    fullSeries() { return this._suppliedSeries || this.series || []; },
    buildCSV() {
      const options = this.buildOptions();
      return fullChartCSV(this.fullSeries(), options,
        new Set(options.series.filter(entry => entry?.hidden).map(entry => entry.name)));
    },
    downloadCSV(csv) {
      const filename = this.chart?.w?.config?.chart?.toolbar?.export?.csv?.filename || this.chart?.w?.globals?.chartID || "chart";
      return downloadFile(new Blob([csv], { type: "text/csv;charset=utf-8" }), `${filename}.csv`, "text/csv;charset=utf-8");
    },
    renderSeries(options) {
      const full = this.fullSeries();
      const budget = Math.max(400, Math.min(2000, Math.round((this.$el.clientWidth || 750) * 2)));
      const sampled = options.xaxis && options.xaxis.type === "datetime"
        ? sampleChartSeries(full, budget, this._range, options.stroke && options.stroke.curve)
        : full;
      return sampled.map(entry => typeof entry === "object" && entry !== null
        ? { ...entry, hidden: this._hiddenSeries.has(entry.name) ||
            (!this._shownSeries?.has(entry.name) && entry.hidden === true) }
        : entry);
    },
    buildOptions() {
      const options = this.extend(this.options || {}, this._extraOptions);
      const domain = options.xaxis?.type === "datetime" && chartDomain(this.fullSeries());
      if (domain) {
        if (this._range && (this._range.max < domain.min || this._range.min > domain.max)) this._range = null;
        options.xaxis = { ...options.xaxis, min: options.xaxis.min ?? domain.min, max: options.xaxis.max ?? domain.max };
      }
      if (this.fullSeries().some(entry => entry?.data?.length > 2000) && options.stroke) {
        const curve = options.stroke.curve;
        options.stroke = { ...options.stroke, curve: Array.isArray(curve)
          ? curve.map(value => value === "smooth" ? "straight" : value)
          : curve === "smooth" ? "straight" : curve };
      }
      const sourceEvents = options.chart?.events || {};
      const events = { ...sourceEvents };
      Object.keys(this.$listeners).forEach(name => {
        const original = events[name];
        events[name] = (...args) => { if (original) original(...args); this.$emit(name, ...args); };
      });
      const legendClick = events.legendClick;
      events.legendClick = (context, index, config) => {
        const name = config.config.series[index]?.name;
        if (name === undefined) { if (legendClick) legendClick(context, index, config); return; }
        if (config.globals.collapsedSeriesIndices.includes(index)) {
          this._hiddenSeries.delete(name);
          if (!this._shownSeries) this._shownSeries = new Set();
          this._shownSeries.add(name);
        } else {
          this._hiddenSeries.add(name);
          this._shownSeries?.delete(name);
        }
        if (legendClick) legendClick(context, index, config);
      };
      const zoomed = events.zoomed;
      events.zoomed = (context, axes) => {
        if (Number.isFinite(axes?.xaxis?.min) && Number.isFinite(axes?.xaxis?.max)) {
          this._range = { min: axes.xaxis.min, max: axes.xaxis.max };
          this.queueUpdate();
        }
        if (zoomed) zoomed(context, axes);
      };
      const scrolled = events.scrolled;
      events.scrolled = (context, axes) => {
        if (axes?.xaxis) { this._range = { ...axes.xaxis }; this.queueUpdate(); }
        if (scrolled) scrolled(context, axes);
      };
      const beforeResetZoom = events.beforeResetZoom;
      if (chartDomain(this.fullSeries())) events.beforeResetZoom = (...args) => {
        this._range = null;
        this.queueUpdate();
        const original = beforeResetZoom && beforeResetZoom(...args);
        return original || { xaxis: chartDomain(this.fullSeries()) || {} };
      };
      options.chart = {
        ...options.chart, type: this.type || options.chart?.type || "line",
        width: this.width, height: this.height,
        animations: { enabled: false, animateGradually: { enabled: false }, dynamicAnimation: { enabled: false } },
        events,
      };
      if (this._range) options.xaxis = { ...options.xaxis, ...this._range };
      options.series = this.renderSeries(options);
      return options;
    },
    async init() {
      if (this.chart || this._disposed) return;
      this._updating = true;
      this.chart = markRaw(this.createChart(this.buildOptions()));
      try { await this.chart.render(); }
      catch (error) { if (!this._disposed) console.warn("Chart render failed", error); }
      finally {
        this._updating = false;
        if (this._disposed) this.destroy();
        else if (this._queued) this.queueUpdate();
      }
    },
    queueUpdate() {
      this._queued = true;
      if (this._updating || this._scheduled || this._disposed || document.hidden) return;
      this._scheduled = true;
      this.$nextTick(async () => {
        this._scheduled = false;
        if (this._disposed || document.hidden || this._updating) return;
        this._queued = false;
        if (!this.chart) { await this.init(); return; }
        this._updating = true;
        try {
          // Clear old collapsed indices without rendering. Visibility from the
          // new series is then applied together with the data in one render.
          if (this.chart.resetSeries) this.chart.resetSeries(false, false);
          await this.chart.updateOptions(this.buildOptions(), false, false, false);
        }
        catch (error) { if (!this._disposed) console.warn("Chart update failed", error); }
        finally {
          this._updating = false;
          if (this._disposed) this.destroy();
          else if (this._queued) this.queueUpdate();
        }
      });
    },
    updateSeries(series) { this._suppliedSeries = series; this.queueUpdate(); },
    updateOptions(options) {
      if (options.series) this._suppliedSeries = options.series;
      const { series, ...other } = options;
      this._extraOptions = this.extend(this._extraOptions, other);
      this.queueUpdate();
    },
    hideSeries(name) {
      if (this._hiddenSeries.has(name) ||
          (!this._shownSeries?.has(name) && this.fullSeries().some(entry => entry?.name === name && entry.hidden))) return;
      this._hiddenSeries.add(name); this._shownSeries?.delete(name); this.queueUpdate();
    },
    showSeries(name) {
      this._hiddenSeries.delete(name);
      if (!this._shownSeries) this._shownSeries = new Set();
      this._shownSeries.add(name); this.queueUpdate();
    },
    toggleSeries(name) {
      const hidden = this._hiddenSeries.has(name) || (!this._shownSeries?.has(name) &&
        this.fullSeries().some(entry => entry?.name === name && entry.hidden));
      if (hidden) this.showSeries(name); else this.hideSeries(name);
      return hidden;
    },
    destroy() { if (this.chart) this.chart.destroy(); this.chart = null; },
  },
};
